import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBogoById } from '../../Webservice/offerAPI';
import { API_URL } from '../../Utils/url';
import { 
    Box, Typography, Paper, Grid, Card, Divider, Avatar, 
    Breadcrumbs, Link as MuiLink, Button, Chip, CircularProgress 
} from '@mui/material';
import { MdArrowBack, MdEdit } from "react-icons/md";
import { motion } from 'framer-motion';

const ViewBogoOffer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [bogo, setBogo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBogoDetails = async () => {
            try {
                const response = await getBogoById(id);
                if (response && (response.bogo || response.data)) {
                    setBogo(response.bogo || response.data);
                }
            } catch (error) {
                console.error("Failed to fetch BOGO details", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBogoDetails();
    }, [id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!bogo) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5">BOGO offer not found</Typography>
                <Button onClick={() => navigate('/offers/bogo')} sx={{ mt: 2 }} variant="contained">Go Back</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, background: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Mulish, sans-serif' }}>
                        BOGO Offer Details
                    </Typography>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                        <MuiLink underline="hover" color="inherit" onClick={() => navigate('/offers/bogo')} sx={{ cursor: 'pointer' }}>
                            BOGO Offers
                        </MuiLink>
                        <Typography color="text.primary">View BOGO</Typography>
                    </Breadcrumbs>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<MdEdit />} 
                        onClick={() => navigate(`/offers/bogo/edit/${id}`)}
                        sx={{ background: 'rgb(255 209 2)', color: 'black', fontWeight: 700, borderRadius: '10px' }}
                    >
                        Edit BOGO
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={<MdArrowBack />} 
                        onClick={() => navigate('/offers/bogo')}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                    >
                        Back to List
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e5e7eb', height: '100%' }}>
                        <Typography variant="h6" fontWeight={800} fontFamily="Mulish" mb={2}>
                            BOGO Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Title</Typography>
                            <Typography variant="body1" fontWeight={700} color="#1a1c1e">{bogo.title}</Typography>
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Description</Typography>
                            <Typography variant="body2" color="#4b5563">{bogo.description || 'No description provided.'}</Typography>
                        </Box>
                        
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, background: '#f3f4f6', borderRadius: '8px', textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">Buy Qty</Typography>
                                    <Typography variant="h6" color="#1a1c1e" fontWeight={800}>{bogo.buyQuantity}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, background: 'rgba(5, 150, 105, 0.1)', borderRadius: '8px', textAlign: 'center' }}>
                                    <Typography variant="caption" color="#059669" fontWeight={700} display="block">Get Qty</Typography>
                                    <Typography variant="h6" color="#059669" fontWeight={800}>{bogo.getQuantity}</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        
                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ p: 2, background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d', textAlign: 'center' }}>
                                <Typography variant="caption" color="#b45309" fontWeight={700} display="block">Discount</Typography>
                                <Typography variant="h5" color="#b45309" fontWeight={800}>{bogo.discountPercentage}% OFF</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={bogo.isActive ? "Active" : "Inactive"} color={bogo.isActive ? "primary" : "default"} sx={{ fontWeight: 700 }} />
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e5e7eb', height: '100%' }}>
                        <Typography variant="h6" fontWeight={800} fontFamily="Mulish" mb={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            Target Items (Buy & Get)
                            <Chip label={`${bogo.buyProducts?.length || 0} Items`} size="small" sx={{ background: 'rgb(255 209 2)', fontWeight: 800 }} />
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={2}>
                            {bogo.buyProducts?.map((item, index) => {
                                const target = item.product_id || item.category_id;
                                if (!target) return null;
                                const isProduct = !!item.product_id;
                                
                                return (
                                    <Grid item xs={12} sm={6} key={target._id || index}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <Card sx={{ display: 'flex', alignItems: 'center', p: 1.5, border: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                                {isProduct && (
                                                    <Avatar 
                                                        src={target.product_images?.[0] ? (target.product_images[0].startsWith('http') ? target.product_images[0] : `${API_URL.replace(/\/api\/?$/, '/')}${target.product_images[0]}`) : '/img/default-product.png'} 
                                                        variant="rounded" 
                                                        sx={{ width: 60, height: 60, mr: 2 }}
                                                    />
                                                )}
                                                <Box sx={{ flex: 1, ml: isProduct ? 0 : 2 }}>
                                                    <Typography variant="body1" fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                                        {target.title || target.name || 'Unknown Item'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Type: {isProduct ? 'Product' : 'Category'}
                                                    </Typography>
                                                </Box>
                                            </Card>
                                        </motion.div>
                                    </Grid>
                                );
                            })}
                            {(!bogo.buyProducts || bogo.buyProducts.length === 0) && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                        No items found in this offer.
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ViewBogoOffer;
